import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import PageContainer from "../../../components/PageContainer";
import AppDataTable from "../../../components/AppDataTable";
import {
  actualizarCategoria,
  eliminarCategoria,
  getCategorias,
  insertarCategoria,
} from "../../../services/modules/Categorias";

const Categorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ id: null, nombre: "", descripcion: "" });

  const [search, setSearch] = useState("");
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreen = isXs;

  const fetchCategorias = async () => {
    setLoading(true);
    try {
      const data = await getCategorias();
      setCategorias(Array.isArray(data) ? data : []);
      setFiltered(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar marcas:", e);
      setCategorias([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategorias();
  }, []);

  useEffect(() => {
    const q = (search || "").toLowerCase().trim();
    if (!q) {
      setFiltered(categorias);
      return;
    }
    setFiltered(
      (categorias || []).filter((c) =>
        [c.nombre, c.descripcion]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
    );
  }, [search, categorias]);

  const handleOpen = (cat = null) => {
    if (cat) {
      setForm({
        id: cat.id_categoria,
        nombre: cat.nombre || "",
        descripcion: cat.descripcion || "",
      });
    } else {
      setForm({ id: null, nombre: "", descripcion: "" });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!String(form.nombre).trim()) {
        alert("El nombre es obligatorio.");
        return;
      }
      if (form.id) {
        await actualizarCategoria(form.id, { nombre: form.nombre, descripcion: form.descripcion });
        alert("Marca actualizada.");
      } else {
        await insertarCategoria({ nombre: form.nombre, descripcion: form.descripcion });
        alert("Marca creada.");
      }
      await fetchCategorias();
      handleClose();
    } catch (e) {
      console.error(e);
      alert("Ocurrio un error al guardar la marca.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Eliminar marca?")) {
      try {
        await eliminarCategoria(id);
        alert("Marca eliminada.");
        await fetchCategorias();
      } catch (e) {
        console.error(e);
        alert("Ocurrio un error al eliminar la marca.");
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "marca",
        headerName: "Marca",
        flex: 1.5,
        minWidth: 240,
        sortable: true,
        valueGetter: (_value, row) => row?.nombre || "",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 36, height: 36 }}>{row.nombre?.[0]?.toUpperCase() || "?"}</Avatar>
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {row.nombre}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.descripcion || "Sin descripcion"}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "descripcion",
        headerName: "Descripción",
        flex: 1.8,
        minWidth: 260,
        sortable: true,
        valueGetter: (_value, row) => row?.descripcion || "",
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary">
            {row.descripcion || "Sin descripcion"}
          </Typography>
        ),
      },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 160,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Editar">
              <IconButton color="primary" size="small" onClick={() => handleOpen(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton
                color="error"
                size="small"
                onClick={() => handleDelete(row.id_categoria)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleDelete]
  );

  return (
    <PageContainer>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <TextField
            placeholder="Buscar por nombre o descripcion"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: !!search && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearch("")} edge="end" size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setSearch("")}>
              Limpiar
            </Button>
            <Button variant="contained" onClick={() => handleOpen()}>
              Nueva Marca
            </Button>
          </Stack>
        </Stack>

        {isXs ? (
          <Stack spacing={1.5}>
            {(loading ? Array.from({ length: 3 }) : filtered).map((row, index) =>
              loading ? (
                <Paper key={index} sx={{ p: 2, borderRadius: 2, boxShadow: 0 }}>
                  <Skeleton variant="rounded" height={96} />
                </Paper>
              ) : (
                <Paper
                  key={row.id_categoria}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {row.nombre}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {row.descripcion || "Sin descripcion"}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" color="primary" onClick={() => handleOpen(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(row.id_categoria)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              )
            )}
          </Stack>
        ) : (
          <AppDataTable
            rows={filtered}
            columns={columns}
            loading={loading}
          />
        )}
      </Stack>

      <Dialog open={open} onClose={handleClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
        <DialogTitle>{form.id ? "Editar Marca" : "Nueva Marca"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Nombre"
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Descripcion"
            name="descripcion"
            value={form.descripcion}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {form.id ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Categorias;
